import { Button } from '@kluein/klue-ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppUIContext } from './appUIContext';
import { useSessionTimeoutWarning } from './useSessionTimeoutWarning';

import type { SessionTimeoutWarning } from './useSessionTimeoutWarning';
import type { ToastPropType } from '@kluein/klue-ui';

/**
 * Component that shows toast notifications for session timeouts. useSessionTimeoutWarning owns
 * the countdown and the sign-out; this renders its warning as a klue-ui toast.
 *
 * Must be rendered inside AppUIProvider to have access to the toast context. Render it once per
 * page: App.tsx mounts it for Current Compete, and Compete 2.0 has its own SessionTimeoutNotifier.
 */
export function SessionTimeoutNotifier(): null {
  const { t } = useTranslation(['Session']);
  const { toast } = useAppUIContext();
  const toastIdRef = useRef<string | null>(null);

  // Store latest toast ref to avoid stale closures
  const toastRef = useRef(toast);

  useEffect(() => {
    toastRef.current = toast;
  });

  const closeToast = (): void => {
    if (toastIdRef.current) {
      toastRef.current.close(toastIdRef.current, true);
      toastIdRef.current = null;
    }
  };

  useSessionTimeoutWarning({
    onWarn: ({ kind, remainingTime, onAction }: SessionTimeoutWarning) => {
      const toastData: Record<SessionTimeoutWarning['kind'], ToastPropType> = {
        idle: {
          message: t('Session:timeout.idle.message', { remainingTime }),
          action: (
            <Button
              alignSelf="end"
              variant="primary"
              onClick={onAction}
              label={t('Session:timeout.idle.button')}
            />
          ),
          // No onClose - dismissing the toast should not refresh the session.
          // User must click the button to stay signed in.
        },
        absolute: {
          message: t('Session:timeout.absolute.message', { remainingTime }),
          action: (
            <Button
              alignSelf="end"
              variant="warning"
              onClick={onAction}
              label={t('Session:timeout.absolute.button')}
            />
          ),
          // No onClose - dismissing the toast should not force sign out.
          // The timer will still sign them out when the time expires.
        },
      };

      // Close previous toast before opening new one to prevent stacking
      // Skip onClose callback to avoid triggering sign out on refresh
      closeToast();
      toastIdRef.current = toastRef.current.open({
        title: t('Session:timeout.title'),
        ...toastData[kind],
      });
    },
    onClear: closeToast,
  });

  return null;
}
